// Open-issue and SLA-breach counts for the workspace.
//
// The app bar badges Issues with the number of open issues whose SLA has
// already been missed, and the Issues page header repeats it as "N breaching
// SLA" (design handoff options 6a / 7c). Both read this one listener rather
// than each opening their own query on the same collection.
//
// "Open" is the four non-resolved states IssuesContainer already treats as
// open. A breach is the *current* clock against the deadline for the state the
// issue is in — response while it is reported/assigned, resolution once someone
// has acknowledged it — so the count has to be recomputed on a timer, not only
// when a document changes: an issue silently goes overdue with no write.
import { useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, query, where } from 'firebase/firestore';
import app from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureAccess } from './useFeatureAccess';
import useClaimsReadySnapshot from './useClaimsReadySnapshot';

const OPEN_STATES = ['reported', 'assigned', 'acknowledged', 'in_progress'];
const RESPONSE_STATES = ['reported', 'assigned'];

/** Deadline that applies to an issue right now, or null when no SLA policy. */
export function issueDeadline(issue) {
  if (RESPONSE_STATES.includes(issue?.status)) {
    return issue?.sla_response_by ? new Date(issue.sla_response_by) : null;
  }
  return issue?.sla_resolution_by ? new Date(issue.sla_resolution_by) : null;
}

export function isBreaching(issue, now = Date.now()) {
  const deadline = issueDeadline(issue);
  return Boolean(deadline) && deadline.getTime() < now;
}

export default function useIssueAlerts() {
  const { subscriberId } = useAuth();
  const { hasIssueTracking } = useFeatureAccess();
  const [issues, setIssues] = useState([]);
  const [tick, setTick] = useState(0);

  // Mounted in the app bar, so it waits for the token to carry the
  // workspace and listens again after a refusal (useClaimsReadySnapshot).
  const { status } = useClaimsReadySnapshot(
    (sid) => (hasIssueTracking
      ? query(
        collection(getFirestore(app), 'subscribers', sid, 'issues'),
        where('deleted', '==', false),
        where('status', 'in', OPEN_STATES)
      )
      : null),
    {
      subscriberId: subscriberId || null,
      label: 'Issue alert counts',
      onNext: (snapshot) => setIssues(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
      onError: (error) => {
        // A workspace without the feature, or rules denying the read, must not
        // take the app bar down with it.
        console.warn('Issue alert counts unavailable:', error?.message);
        setIssues([]);
      },
    },
    [Boolean(hasIssueTracking)]
  );
  // Nothing to count with the feature off, and nothing of another workspace's.
  useEffect(() => {
    if (status === 'idle') setIssues((prev) => (prev.length ? [] : prev));
  }, [status]);
  useEffect(() => {
    setIssues((prev) => (prev.length ? [] : prev));
  }, [subscriberId]);

  // Re-evaluate every minute so a deadline passing shows up without a write.
  useEffect(() => {
    if (issues.length === 0) return undefined;
    const timer = setInterval(() => setTick(t => t + 1), 60 * 1000);
    return () => clearInterval(timer);
  }, [issues.length]);

  return useMemo(() => {
    const now = Date.now();
    return {
      openCount: issues.length,
      breachingCount: issues.filter(issue => isBreaching(issue, now)).length,
    };
    // `tick` is the recompute trigger; it is deliberately in the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issues, tick]);
}
